import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface SourceRawPopoverProps {
  /** Raw source attribution tags (UTM params, referrer, click IDs) from `deriveSource`. */
  raw?: Record<string, string>
  /** The channel label/badge that triggers the popover on hover, focus, or click. */
  children: ReactNode
}

/**
 * Hover/focus popover showing the raw attribution tags (UTM/referrer/click IDs)
 * behind a derived source channel. Shared between the lead detail page's
 * "Original Inquiry" card and the pipeline job's "Original Inquiry" card so
 * both surface the same raw tags the same way.
 *
 * Renders `children` unwrapped (no popover) when there are no raw tags to show.
 */
export function SourceRawPopover({ raw, children }: SourceRawPopoverProps) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rawJson = raw && Object.keys(raw).length > 0 ? JSON.stringify(raw, null, 2) : undefined

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  if (!rawJson) return <>{children}</>

  const openPopover = () => {
    clearCloseTimer()
    setOpen(true)
  }

  const closePopover = () => {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => setOpen(false), 100)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="View raw attribution data"
          className="-m-1 inline-flex rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={(event) => {
            event.preventDefault()
            openPopover()
          }}
          onMouseEnter={openPopover}
          onMouseLeave={closePopover}
          onFocus={openPopover}
          onBlur={closePopover}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="w-[32rem] max-w-[calc(100vw-2rem)] p-3"
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">Raw attribution data</p>
          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed whitespace-pre-wrap break-all">
            {rawJson}
          </pre>
        </div>
      </PopoverContent>
    </Popover>
  )
}
